import {
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';
import { Capacitor } from '@capacitor/core';

// =====================================================================
// AUTH HELPERS — wraps Firebase Auth + ensures a Firestore user doc
// =====================================================================
/**
 * Map Firebase provider IDs to friendly names.
 */
function providerName(id) {
  if (id === 'google.com') return 'Google';
  if (id === 'password') return 'Email & password';
  if (id === 'phone') return 'Phone OTP';
  return id;
}

/**
 * If sign-in fails because the email already exists with a different provider,
 * tell the caller which provider to use instead.
 */
async function detectProviderConflict(email, attemptedProvider) {
  if (!email) return null;
  try {
    const methods = await fetchSignInMethodsForEmail(auth, email);
    if (methods.length === 0) return null;
    if (methods.includes(attemptedProvider)) return null;  // no conflict
    return {
      existing: methods[0],            // e.g. 'google.com'
      friendly: providerName(methods[0]),
      email,
    };
  } catch {
    return null;
  }
}

/**
 * Google Sign-In — platform-aware.
 * On native (Capacitor Android/iOS): uses the Capacitor Firebase Authentication
 * plugin which invokes the Android Google Sign-In SDK natively, then exchanges
 * the resulting ID token for a Firebase credential. This is the proper way to
 * do Google Sign-In inside a WebView; signInWithPopup doesn't work natively.
 *
 * On web (PWA / browser): uses Firebase's signInWithPopup as before.
 *
 * In both cases, the resulting Firebase user is funneled through ensureUserDoc
 * so a Firestore profile is created on first sign-in.
 */
export async function signInWithGoogle() {
  try {
    let user;
    if (Capacitor.isNativePlatform()) {
      // Native path — use Capacitor Firebase Authentication plugin
      // Dynamic import keeps web bundle small; plugin only loads on native
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle();
      // Plugin returns an idToken which we exchange for a Firebase credential
      if (!result.credential?.idToken) {
        throw new Error('Google sign-in returned no ID token');
      }
      const credential = GoogleAuthProvider.credential(result.credential.idToken);
      const fbResult = await signInWithCredential(auth, credential);
      user = fbResult.user;
    } else {
      // Web path — use Firebase's popup flow (unchanged)
      const result = await signInWithPopup(auth, googleProvider);
      user = result.user;
    }
    await ensureUserDoc(user);
    return user;
  } catch (err) {
    // Firebase returns 'auth/account-exists-with-different-credential' for cross-provider conflicts
    if (err.code === 'auth/account-exists-with-different-credential') {
      const email = err.customData?.email;
      const conflict = await detectProviderConflict(email, 'google.com');
      if (conflict) {
        const friendly = new Error(`You already have an Aangan account using ${conflict.friendly} with this email. Sign in with ${conflict.friendly} instead.`);
        friendly.code = 'aangan/account-exists-different-provider';
        friendly.existingProvider = conflict.existing;
        friendly.friendlyProvider = conflict.friendly;
        throw friendly;
      }
    }
    throw err;
  }
}

export async function startPhoneSignIn(phoneNumber, containerId) {
  if (window._recaptchaVerifier) {
    try { window._recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
  }
  const verifier = new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
  window._recaptchaVerifier = verifier;
  return await signInWithPhoneNumber(auth, phoneNumber, verifier);
}

export async function confirmPhoneOtp(confirmationResult, otp) {
  const result = await confirmationResult.confirm(otp);
  await ensureUserDoc(result.user);
  return result.user;
}

export async function signUpWithEmail(email, password) {
  // Check if email is already in use with another provider before attempting
  const conflict = await detectProviderConflict(email, 'password');
  if (conflict) {
    const err = new Error(`You already have an Aangan account using ${conflict.friendly} with this email. Sign in with ${conflict.friendly} instead.`);
    err.code = 'aangan/account-exists-different-provider';
    err.existingProvider = conflict.existing;
    err.friendlyProvider = conflict.friendly;
    throw err;
  }
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await ensureUserDoc(result.user);
  return result.user;
}

export async function signInWithEmail(email, password) {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    // If sign-in failed with invalid credential, check if the email exists with a different provider
    if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
      const conflict = await detectProviderConflict(email, 'password');
      if (conflict) {
        const friendly = new Error(`You signed up with ${conflict.friendly}, not email & password. Use ${conflict.friendly} to sign in.`);
        friendly.code = 'aangan/account-exists-different-provider';
        friendly.existingProvider = conflict.existing;
        friendly.friendlyProvider = conflict.friendly;
        throw friendly;
      }
    }
    throw err;
  }
}

export async function sendPasswordReset(email) {
  return await sendPasswordResetEmail(auth, email);
}

/**
 * Sign out — also signs out from the native Google plugin if on native
 * so the next sign-in actually shows the account picker again.
 */
export async function logout() {
  if (Capacitor.isNativePlatform()) {
    try {
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      await FirebaseAuthentication.signOut();
    } catch (e) {
      console.warn('Native plugin sign-out failed:', e);
    }
  }
  return await signOut(auth);
}

export function watchAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

/**
 * Load the current user's Firestore profile doc.
 */
export async function loadUserDoc(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Mark a user as having completed onboarding (privacy + profile + first batch).
 */
export async function markUserOnboarded(uid) {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid), {
    onboardedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Update arbitrary fields on a user's profile (status, currentCity, name, photoUrl, etc).
 * Only writes the fields you pass. Doesn't touch anything else.
 */
export async function updateUserProfile(uid, fields) {
  if (!uid) return;
  await setDoc(doc(db, 'users', uid), fields, { merge: true });
}

/**
 * Internal: make sure the user has a Firestore doc.
 * Called automatically after every sign-in / sign-up path.
 */
async function ensureUserDoc(user) {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // IMPORTANT: We DO NOT seed `name` from user.displayName. Aangan's name is
    // chosen by the user in ProfileSetup. If we seeded with Google's displayName,
    // a user who skipped ProfileSetup somehow would have the Google name stuck
    // as their Aangan name. We leave `name` absent until ProfileSetup writes it.
    // Same logic for photoUrl — only set if the user explicitly uploaded one
    // via PhotoCropModal in ProfileSetup.
    await setDoc(ref, {
      bio: '',
      status: '',
      currentCity: '',
      color: '#C45A3F',
      blockedUserIds: [],
      fcmTokens: [],
      scheduledForDeletionAt: null,
      createdAt: serverTimestamp(),
    });
  }
}